import localVersion from '../version.json';

let isRefreshing = false;

/**
 * Perform a clean reload of the page
 */
export function reloadApp() {
  if (isRefreshing) return;
  isRefreshing = true;
  console.log('[VersionWatchdog] Reloading app to apply latest version...');
  window.location.reload();
}

/**
 * Check if the server has a newer version.json than the running client
 */
export async function checkForAppUpdate() {
  try {
    const response = await fetch(`/version.json?_t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });

    if (!response.ok) {
      return false;
    }

    const remoteVersion = await response.json();

    const isNewer =
      remoteVersion.buildTime &&
      localVersion.buildTime &&
      remoteVersion.buildTime > localVersion.buildTime;

    const isDifferentVersion =
      remoteVersion.version &&
      localVersion.version &&
      remoteVersion.version !== localVersion.version;

    if (isNewer || isDifferentVersion) {
      console.log('[VersionWatchdog] New version detected on server:', {
        local: localVersion,
        remote: remoteVersion
      });

      // If a service worker is present, trigger update check on SW as well
      if ('serviceWorker' in navigator && Boolean(navigator.serviceWorker)) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            if (registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            } else {
              await registration.update();
            }
          }
        } catch (swErr) {
          console.warn('[VersionWatchdog] SW update check failed:', swErr);
        }
      }

      // Fail-safe: if controllerchange doesn't trigger reload within 2.5s, force reload
      setTimeout(() => {
        reloadApp();
      }, 2500);

      return true;
    }

    return false;
  } catch (error) {
    // Silent fail if network is offline
    return false;
  }
}

/**
 * Initialize background version watchdog listeners and ChunkLoadError recovery
 */
export function initVersionWatchdog() {
  // 1. Initial check shortly after app load
  setTimeout(() => {
    checkForAppUpdate();
  }, 5000);

  // 2. Periodic background check every 5 minutes
  const POLL_INTERVAL = 5 * 60 * 1000;
  setInterval(() => {
    checkForAppUpdate();
  }, POLL_INTERVAL);

  // 3. Check whenever user returns to tab / PWA comes to foreground
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForAppUpdate();
    }
  });

  window.addEventListener('focus', () => {
    checkForAppUpdate();
  });

  // 4. Inevitable recovery from ChunkLoadError (when old deployment chunks 404 on server)
  window.addEventListener('error', (event) => {
    const errorMsg = event.message || '';
    if (
      errorMsg.includes('Loading chunk') ||
      errorMsg.includes('ChunkLoadError') ||
      errorMsg.includes('failed to fetch dynamically imported module')
    ) {
      console.warn('[VersionWatchdog] ChunkLoadError detected. Forcing reload to latest version.');
      reloadApp();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || String(event.reason || '');
    if (
      reason.includes('Loading chunk') ||
      reason.includes('ChunkLoadError') ||
      reason.includes('failed to fetch dynamically imported module')
    ) {
      console.warn('[VersionWatchdog] Unhandled ChunkLoadError rejection detected. Forcing reload.');
      reloadApp();
    }
  });
}
